/*
  Repaints every Shopify "Sign in" / "Sign in with Shop" control from Shop
  brand purple (#5433EB) onto the Veaudry gold ramp.

  WHY THIS IS A SEPARATE FILE FROM chat-launcher.js
  These controls are NOT confined to the chat widget. They mount in at
  least three places, each in its own shadow root, and the two that matter
  are in different subtrees:

    1. shopify-chat > ... > shopify-sign-in-button   the chat header pill
    2. shopify-chat > ... > shop-login               "Sign in with Shop",
                                                     in the chat's login panel
    3. <document level> > ... > shop-login           the SAME popover, opened
                                                     from the header account
                                                     icon — NOT under
                                                     shopify-chat at all

  chat-launcher.js only ever walks roots under <shopify-chat>, because that
  is all it needs for the orb. Case 3 is outside that subtree, so styling
  it from there is not a matter of adding a selector — the stylesheet is
  never injected into the right root in the first place. That was the bug:
  the chat's own buttons went gold while the header popover stayed purple.

  So this file walks the WHOLE document instead, and owns all sign-in
  styling. chat-launcher.js keeps the orb, the panel surface and the
  backdrop; it deliberately no longer carries sign-in rules, so there is
  one source of truth rather than two that drift apart.

  WHY A SCRIPT AND NOT A STYLESHEET
  Every one of these lives behind at least one shadow boundary, and a
  theme stylesheet does not cross one. A <style> has to be placed inside
  each root individually. That is the whole job of this file.

  WHAT THIS DOES NOT COVER
  The account pages themselves (/account/login and friends) are NOT part
  of the theme on this store — it runs Shopify's new customer accounts, so
  /account/login leaves for shopify.com and is branded from Shopify admin >
  Settings > Customer accounts. Nothing here reaches those, by design.
  templates/customers/*.liquid are dead code for the same reason.

  FRAGILITY, STATED PLAINLY
  This is unsupported styling of Shopify's own shadow DOM. If Shopify
  restructures these components the buttons revert to purple — they do not
  break, they just stop being gold. Selectors below prefer stable hooks
  (data-testid, semantic class names) over build-generated utility classes
  precisely to make that less likely.
*/
(function () {
  'use strict';

  var STYLE_MARK = 'data-v-signin-skin';

  /* Hardcoded rather than var(--accent-gold-*). These rules are injected
     into Shopify's roots, and while custom properties do inherit across a
     shadow boundary, that only holds while theme-mode.css has resolved.
     These controls can mount before it does. Same values as the tokens:
     --accent-gold-start #e1a03e, --accent-gold-end #ebd27b,
     --on-gold-text #14110D. */
  var GOLD =
    'background:linear-gradient(135deg,#e1a03e 0%,#ebd27b 100%)!important;' +
    /* Fallback for anything that ignores background-image. */
    'background-color:#e1a03e!important;' +
    'border-color:transparent!important;';

  /* Near-black on gold, not Shopify's white. White on this gold lands
     around 1.6:1. Black-on-gold is one of the three sanctioned brand
     lockups, so this is the on-brand and the legible answer at once. */
  var INK = '#14110d';

  var CSS = [
    /* 1. The chat header pill. Class is semantic and stable enough to use
          directly; the [class*=] catches a rename. */
    '.sign-in-button,.sign-in-button--shop,' +
      'button[class*="sign-in-button"]{' +
      GOLD +
      'color:' +
      INK +
      '!important}',

    '.sign-in-button svg,.sign-in-button svg *,' +
      'button[class*="sign-in-button"] svg *{' +
      'fill:' +
      INK +
      '!important;color:' +
      INK +
      '!important}',

    /* 2. Primary CTA in the chat's new-chat popover. */
    '.new-chat-popover__sign-in,.new-chat-popover__button--primary{' +
      GOLD +
      'color:' +
      INK +
      '!important}',

    /* 3. "Sign in with Shop", in <shop-login> — both the in-chat panel and
          the header popover, which are the same component mounted twice.

          Targeted by data-testid, NOT by class: shop-login is built with
          generated utility classes (bg-purple-primary,
          hover_enabled_bg-purple-d0) that will not survive a Shopify
          rebuild. data-testid="login-button" is a stabler contract. The
          class selector is kept as a fallback, scoped to button so it
          cannot repaint other purple surfaces in the popover. */
    '[data-testid="login-button"],button.bg-purple-primary{' + GOLD + '}',

    /* The label is the words "Sign in with" PLUS the Shop wordmark as an
       inline <svg>, both shipped white. They must flip together — recolour
       only the text and the wordmark is stranded at ~1.6:1 on the gold. */
    '[data-testid="login-button"] span,' +
      '[data-testid="login-button"] svg{color:' +
      INK +
      '!important}',
    '[data-testid="login-button"] svg path{fill:' + INK + '!important}',
  ].join('');

  /* Every shadow root in the document, collected fresh each pass. Fresh
     because these components mount late and mount more roots as they go —
     the header popover's root does not exist until the icon is clicked. */
  function collect(node, acc) {
    var all = node.querySelectorAll('*');

    for (var i = 0; i < all.length; i++) {
      var root = all[i].shadowRoot;
      if (!root) continue;
      acc.push(root);
      collect(root, acc);
    }
    return acc;
  }

  var watched = [];
  var queued = false;

  /* Idempotent via the marker, so re-running every pass is cheap. */
  function injectInto(roots) {
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].querySelector('style[' + STYLE_MARK + ']')) continue;
      var style = document.createElement('style');
      style.setAttribute(STYLE_MARK, '');
      style.textContent = CSS;
      roots[i].appendChild(style);
    }
  }

  /* Mutations inside a shadow root do NOT bubble out to an observer on the
     light DOM. Without an observer per root, a subtree Shopify rebuilds
     (which it does on open) comes back unstyled and flashes purple. */
  function watch(roots) {
    for (var i = 0; i < roots.length; i++) {
      if (watched.indexOf(roots[i]) !== -1) continue;
      watched.push(roots[i]);
      new MutationObserver(schedule).observe(roots[i], {
        childList: true,
        subtree: true,
      });
    }
  }

  function pass() {
    queued = false;
    var roots = collect(document, []);
    injectInto(roots);
    watch(roots);
  }

  /* The walk touches every element in the document and these components
     mutate in bursts while mounting, so coalesce to one pass per frame. */
  function schedule() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(pass);
  }

  pass();

  /* Document-level observer for hosts that appear after load — Shopify's
     customer-accounts script injects its element well after DOMContentLoaded. */
  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  /* The header popover is created on the click that opens it, so its root
     may not exist until the frame after. Cheap insurance on top of the
     observers, and it costs one pass per interaction. */
  document.addEventListener('click', schedule, true);

  /* Debug handle for a live storefront: __vSignIn.pass() re-injects,
     __vSignIn.found() lists the controls currently reachable. */
  window.__vSignIn = {
    pass: pass,
    found: function () {
      var roots = collect(document, []);
      var out = [];

      for (var i = 0; i < roots.length; i++) {
        var hits = roots[i].querySelectorAll(
          '[data-testid="login-button"],.sign-in-button,.new-chat-popover__sign-in'
        );
        for (var j = 0; j < hits.length; j++) out.push(hits[j]);
      }
      return out;
    },
  };
})();
